import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { PouchValueKeyboardPanel } from '../components/PouchValueKeyboardPanel';
import type { PouchValueKeyboardVariant } from '../utils/pouchValueInput';

export interface PouchValueKeyboardSession {
  id: string;
  /** キャンバス上のパウチ編集時: キーボード被り回避のアンカー取得用 */
  pouchId?: string;
  variant: PouchValueKeyboardVariant;
  isTime: boolean;
  insertText: (text: string) => void;
  backspace: () => void;
  confirm: () => void;
  cancel?: () => void;
  /** パウチ値編集時のみ: 値確定してタイトル編集へ（Space と同じ） */
  goToTitle?: () => void;
}

interface SessionMeta {
  id: string;
  variant: PouchValueKeyboardVariant;
  isTime: boolean;
}

interface PouchValueKeyboardContextValue {
  activeSessionId: string | null;
  hardwareKeyboardActive: boolean;
  registerSession: (session: PouchValueKeyboardSession) => void;
  unregisterSession: (id: string) => void;
}

const PouchValueKeyboardContext = createContext<PouchValueKeyboardContextValue | null>(null);

export function usePouchValueKeyboard(): PouchValueKeyboardContextValue | null {
  return useContext(PouchValueKeyboardContext);
}

/** registerSession のたびに Provider 外から触らないようモジュール内 ref */
const sessionsStoreRef: { current: Map<string, PouchValueKeyboardSession> } = {
  current: new Map(),
};

interface PouchValueKeyboardProviderProps {
  isTouchDevice: boolean;
  children: React.ReactNode;
}

export function PouchValueKeyboardProvider({
  isTouchDevice,
  children,
}: PouchValueKeyboardProviderProps) {
  const [activeSessionMeta, setActiveSessionMeta] = useState<SessionMeta | null>(null);
  const [hardwareKeyboardActive, setHardwareKeyboardActive] = useState(false);
  const orderRef = useRef<string[]>([]);

  const registerSession = useCallback((session: PouchValueKeyboardSession) => {
    sessionsStoreRef.current.set(session.id, session);
    if (!orderRef.current.includes(session.id)) {
      orderRef.current.push(session.id);
    }
    setActiveSessionMeta(prev => {
      if (
        prev?.id === session.id &&
        prev.variant === session.variant &&
        prev.isTime === session.isTime
      ) {
        return prev;
      }
      return { id: session.id, variant: session.variant, isTime: session.isTime };
    });
  }, []);

  const unregisterSession = useCallback((id: string) => {
    if (!sessionsStoreRef.current.has(id)) {
      return;
    }
    sessionsStoreRef.current.delete(id);
    orderRef.current = orderRef.current.filter(sid => sid !== id);

    setActiveSessionMeta(prev => {
      if (prev?.id !== id) {
        return prev;
      }
      const remaining = orderRef.current;
      if (remaining.length === 0) {
        return null;
      }
      const lastId = remaining[remaining.length - 1];
      const last = sessionsStoreRef.current.get(lastId);
      if (!last) {
        return null;
      }
      return { id: last.id, variant: last.variant, isTime: last.isTime };
    });
    setHardwareKeyboardActive(false);
  }, []);

  const activeSessionId = activeSessionMeta?.id ?? null;

  useEffect(() => {
    if (!isTouchDevice || !activeSessionId) {
      setHardwareKeyboardActive(false);
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.isTrusted) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('.pouch-value-keyboard-panel')) return;
      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'Meta' ||
        e.key === 'CapsLock'
      ) {
        return;
      }
      setHardwareKeyboardActive(true);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isTouchDevice, activeSessionId]);

  const value = useMemo(
    (): PouchValueKeyboardContextValue => ({
      activeSessionId,
      hardwareKeyboardActive,
      registerSession,
      unregisterSession,
    }),
    [activeSessionId, hardwareKeyboardActive, registerSession, unregisterSession],
  );

  const showPanel =
    isTouchDevice && !!activeSessionId && !hardwareKeyboardActive;

  const [panelSessionId, setPanelSessionId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const panelSessionSnapshotRef = useRef<PouchValueKeyboardSession | null>(null);

  const clearPanel = useCallback(() => {
    setPanelSessionId(null);
    panelSessionSnapshotRef.current = null;
  }, []);

  useLayoutEffect(() => {
    if (!showPanel || !activeSessionId) {
      return;
    }
    const session = sessionsStoreRef.current.get(activeSessionId);
    if (!session) {
      return;
    }
    panelSessionSnapshotRef.current = session;
    setPanelSessionId(activeSessionId);
  }, [showPanel, activeSessionId]);

  useEffect(() => {
    if (!showPanel || !panelSessionId) {
      return;
    }
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => setPanelVisible(true));
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf) cancelAnimationFrame(innerRaf);
    };
  }, [showPanel, panelSessionId]);

  useLayoutEffect(() => {
    if (!showPanel && panelSessionId) {
      setPanelVisible(false);
    }
  }, [showPanel, panelSessionId]);

  useEffect(() => {
    if (panelVisible || !panelSessionId || showPanel) {
      return;
    }
    const timeout = window.setTimeout(() => {
      clearPanel();
    }, 280);
    return () => window.clearTimeout(timeout);
  }, [panelVisible, panelSessionId, showPanel, clearPanel]);

  const handlePanelTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (e.propertyName !== 'opacity' && e.propertyName !== 'filter') {
        return;
      }
      if (panelVisible || showPanel) {
        return;
      }
      clearPanel();
    },
    [panelVisible, showPanel, clearPanel],
  );

  const panelSession = panelSessionId
    ? sessionsStoreRef.current.get(panelSessionId) ?? panelSessionSnapshotRef.current
    : null;

  return (
    <PouchValueKeyboardContext.Provider value={value}>
      {children}
      {panelSession
        ? createPortal(
            <PouchValueKeyboardPanel
              key={panelSessionId}
              session={panelSession}
              visible={panelVisible}
              onTransitionEnd={handlePanelTransitionEnd}
            />,
            document.body,
          )
        : null}
    </PouchValueKeyboardContext.Provider>
  );
}
